// ============================================================
// Virtual Website Tasks
// Synthesize in-memory "tasks" that summarize Website page work
// assigned to the current user.
//
// One virtual task per client where the current user has 1+ pages
// assigned that aren't yet live.
//
// Mirrors the pattern in seoVirtualTasks.js.
// ============================================================

/**
 * @param {string} currentUserId
 * @param {object[]} pages - all seo_pages rows (with client_id, assigned_to, build_status, etc.)
 * @param {object[]} clients - clients (for name lookup + AD check)
 */
export function computeVirtualWebsiteTasks(currentUserId, pages, clients) {
  if (!currentUserId) return []
  const tasks = []
  const clientById = new Map(clients.map(c => [c.id, c]))

  // (A) Pages assigned to me, grouped by client (only count pages that have
  // EITHER build_status set OR build steps started — to avoid surfacing
  // every Yoast row that happens to be assigned to me as a "website task")
  const pagesByClient = new Map()
  for (const p of pages) {
    if (p.assigned_to !== currentUserId) continue
    // Only consider pages that are part of an active website build:
    // (build_status is anything other than null OR aesthetic/content steps started)
    const inBuildFlow = p.build_status && p.build_status !== 'not_started' && p.build_status !== 'live'
    const hasBuildActivity = p.content_written || p.internal_review_done || p.client_review_done || p.awaiting_ad_approval || p.awaiting_client_approval
    if (!inBuildFlow && !hasBuildActivity) {
      // Skip — this page is either fully done, never started, or just a Yoast-only row
      continue
    }
    if (!pagesByClient.has(p.client_id)) pagesByClient.set(p.client_id, [])
    pagesByClient.get(p.client_id).push(p)
  }

  for (const [clientId, clientPages] of pagesByClient.entries()) {
    const client = clientById.get(clientId)
    const earliestDue = clientPages.filter(p => p.due_date).map(p => p.due_date).sort()[0]
    tasks.push({
      id: `virt-website-${clientId}`,
      title: `${clientPages.length} website ${clientPages.length === 1 ? 'page' : 'pages'} to build`,
      description: `Website pages for ${client?.name || 'a client'}`,
      status: 'in_progress',
      assigned_to: currentUserId,
      assigned_by: null,
      client_id: clientId,
      service_area: 'website',
      date_due: earliestDue || null,
      created_at: clientPages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0]?.created_at,
      updated_at: clientPages.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0]?.updated_at,
      is_urgent: false,
      checklist_items: clientPages.slice(0, 5).map(p => ({
        text: p.page_name,
        checked: p.build_status === 'live',
      })),
      _virtual: true,
      _virtualTarget: { type: 'website', clientId },
    })
  }

  // (B) Pages where I'm the AD and someone wants my approval
  const adApprovalsByClient = new Map()
  for (const p of pages) {
    if (!p.awaiting_ad_approval || p.internal_review_done) continue
    const client = clientById.get(p.client_id)
    if (!client || client.account_director_id !== currentUserId) continue
    if (!adApprovalsByClient.has(p.client_id)) adApprovalsByClient.set(p.client_id, [])
    adApprovalsByClient.get(p.client_id).push(p)
  }
  for (const [clientId, awaitingPages] of adApprovalsByClient.entries()) {
    const client = clientById.get(clientId)
    tasks.push({
      id: `virt-website-approve-${clientId}`,
      title: `${awaitingPages.length} website ${awaitingPages.length === 1 ? 'page needs' : 'pages need'} your approval`,
      description: `Website pages awaiting AD approval for ${client?.name || 'a client'}`,
      status: 'ready_for_review',
      assigned_to: currentUserId,
      assigned_by: currentUserId,
      client_id: clientId,
      service_area: 'website',
      date_due: null,
      is_urgent: false,
      checklist_items: awaitingPages.slice(0, 5).map(p => ({
        text: p.page_name,
        checked: false,
      })),
      _virtual: true,
      _virtualTarget: { type: 'website', clientId },
    })
  }

  return tasks
}